import { Controller } from "@hotwired/stimulus"
import { MultiSelect } from "@coreui/coreui-pro"
import { isMultiSelectSyncing, selectEmptyOption, syncMultiSelect } from "../shared/multi_select"

// CoreUI wraps the select, so Stimulus must stay on a stable parent element.
export default class extends Controller {
  static targets = ["select"]
  static values = { requiredMessage: String }

  connect() {
    this.mount()
  }

  disconnect() {
    this.unmount()
  }

  mount() {
    if (this.multiSelect) return

    this.abortController = new AbortController()
    const options = { signal: this.abortController.signal }
    this.changeQueued = false
    this.dispatchingNativeChange = false
    this.selectTarget.addEventListener("coreui:multi-select-refresh", () => this.refresh(), options)
    this.selectTarget.classList.toggle("disabled", this.selectTarget.disabled)
    this.multiSelect = MultiSelect.getOrCreateInstance(this.selectTarget, {
      multiple: this.selectTarget.multiple,
      required: this.selectTarget.required,
      disabled: this.selectTarget.disabled,
      // CoreUI searches option text; retain code searches without changing the labels.
      optionsTemplate: (option) => {
        const label = document.createElement("span")
        label.textContent = option.text
        const code = document.createElement("span")
        code.className = "visually-hidden"
        code.textContent = ` ${option.value}`
        label.append(code)
        return label.outerHTML
      }
    })

    this.selectTarget.addEventListener("changed.coreui.multi-select", (event) => this.coreUIChanged(event), options)
    this.selectTarget.addEventListener("change", () => this.nativeChanged(), options)
    this.selectTarget.form?.addEventListener("reset", () => this.formReset(), options)

    this.configureSearchInput()
    this.validate()
  }

  configureSearchInput() {
    const search = this.selectTarget.parentElement?.querySelector(".form-multi-select-search")
    if (search) {
      search.removeAttribute("name")
      const options = { signal: this.abortController.signal }
      let composing = false
      search.addEventListener("compositionstart", () => {
        composing = true
        this.selectTarget.dispatchEvent(new CustomEvent("coreui:multi-select-composition-start"))
      }, options)
      search.addEventListener("compositionend", () => {
        composing = false
        this.multiSelect.search(search.value)
      }, options)
      // CoreUI handles keyup on the input and navigation on its ancestors.
      // Leave IME candidate keys to the browser without cancelling their defaults.
      const guardComposition = (event) => {
        if (composing || event.isComposing || event.keyCode === 229) {
          event.stopImmediatePropagation()
        }
      }
      for (const type of ["keydown", "keyup"]) {
        search.addEventListener(type, guardComposition, { ...options, capture: true })
      }
      search.addEventListener("keydown", (event) => {
        if (event.key === "Enter") event.preventDefault()
      }, options)
    }
  }

  unmount() {
    this.abortController?.abort()
    this.multiSelect?.dispose()
    this.multiSelect = null
    this.changeQueued = false
  }

  refresh() {
    this.unmount()
    this.mount()
  }

  coreUIChanged(event) {
    if (!this.selectTarget.multiple && event.value?.length === 0) {
      selectEmptyOption(this.selectTarget)
    }
    this.validate()
    if (isMultiSelectSyncing(this.selectTarget) || this.changeQueued) return

    // CoreUI emits once per option during select-all. Coalesce the synchronous
    // burst into the single native event expected from a select interaction.
    this.changeQueued = true
    queueMicrotask(() => {
      if (!this.multiSelect || !this.changeQueued) return

      this.changeQueued = false
      this.dispatchingNativeChange = true
      this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.dispatchingNativeChange = false
    })
  }

  nativeChanged() {
    this.validate()
    if (this.dispatchingNativeChange || isMultiSelectSyncing(this.selectTarget)) return

    syncMultiSelect(this.selectTarget)
  }

  formReset() {
    requestAnimationFrame(() => {
      if (!this.multiSelect) return

      syncMultiSelect(this.selectTarget)
      this.validate()
    })
  }

  validate() {
    const hasValue = Array.from(this.selectTarget.selectedOptions).some((option) => option.value !== "")
    this.selectTarget.setCustomValidity(this.selectTarget.required && !hasValue
      ? this.requiredMessageValue
      : "")
  }
}
